import type { PosicaoGeradorCortina } from './pontosSrs'
import { limitar, vetor, type Vetor3 } from '../geometria/vetor3'
import { rotuloLadoVeiculo } from './rotulosLado'

/**
 * Perfis didáticos de veículo.
 *
 * Convenção de eixos (metros, mesmo referencial do cenário JSON):
 *   +Z = frente do veículo      -Z = traseira
 *   +X = lado esquerdo (motorista)   -X = lado direito (passageiro)
 *
 * Doutrina adotada: "lado do motorista" é o lado em que o motorista dirige. As
 * âncoras, as portas "Esquerda", a estrutura e o SRS do motorista ficam em +X.
 *   +Y = para cima, 0 = solo
 *
 * Este módulo é TypeScript puro: não importa React nem Three.js.
 * A geometria da cena 3D deve derivar daqui, nunca de números soltos em JSX.
 */

export interface AncoraOcupante {
  id: string
  nome: string
  /** centro da pelve do ocupante, em coordenadas do veículo */
  posicao: Vetor3
  /** rotação da âncora em radianos (Euler YXZ); normalmente só yaw */
  rotacao: Vetor3
  /** altura do topo do assento, usada para apoiar o manequim */
  alturaAssento: number
  lado: 'esquerdo' | 'direito' | 'central'
  fila: 'dianteira' | 'traseira'
}

/**
 * Quais dispositivos SRS existem *neste perfil didático*.
 *
 * Nenhum veículo real deve ser descrito como portador de todos eles.
 * A maquete é configurável e a UI precisa dizer isso ao aluno.
 */
export interface ConfiguracaoSrs {
  airbagMotorista: boolean
  airbagPassageiro: boolean
  airbagLateralEsquerdo: boolean
  airbagLateralDireito: boolean
  airbagCortinaEsquerdo: boolean
  airbagCortinaDireito: boolean
  airbagBancoMotorista: boolean
  airbagColunaMotorista: boolean
}

/**
 * Conjunto de retenção de UMA posição de ocupante.
 *
 * Como nos airbags, isto é configuração do perfil didático: nem toda posição
 * tem cinto de três pontos, e pretensionador em banco traseiro está longe de
 * ser universal. A maquete não deve sugerir o contrário.
 */
export type TipoCinto = 'tresPontos' | 'subabdominal'

export interface ConfiguracaoCinto {
  tipo: TipoCinto
  /** pretensionador de retrator: fica no carretel, no pé da coluna ou na prateleira */
  pretensionador: boolean
  /** pretensionador de fecho: tubo junto ao fecho, preso ao trilho do banco */
  pretensionadorFecho?: boolean
}

/** Retenção por id de âncora de ocupante. */
export type ConfiguracaoRetencao = Record<string, ConfiguracaoCinto>

/** Como o veículo se move, o que decide o que existe sob o assoalho. */
export type Tracao = 'dianteira' | 'traseira' | 'integral'

export type Propulsao = 'combustao' | 'hibrido' | 'eletrico'

/**
 * Fontes de energia presentes *neste perfil didático*.
 *
 * Como nos airbags, isto é configuração e não descrição do gênero: nenhum
 * veículo carrega tudo ao mesmo tempo, e a maquete precisa dizer ao aluno o que
 * está mostrando. O que existe na ocorrência se confirma na inspeção e na ficha
 * de salvamento, nunca por semelhança com esta maquete.
 */
export interface ConfiguracaoEnergia {
  propulsao: Propulsao
  tanqueCombustivel: boolean
  /** cilindro de GNV, comum na frota brasileira e alojado no porta-malas */
  cilindroGnv: boolean
  /** pacote de alta tensão de híbrido ou elétrico, sob o assoalho */
  bateriaAltaTensao: boolean
}

export interface FaixaLongitudinal {
  zDianteiro: number
  zTraseiro: number
}

export interface PerfilVeiculo {
  id: 'hatchCompacto' | 'sedanMedio'
  nome: string
  comprimento: number
  largura: number
  altura: number
  entreEixos: number
  eixoDianteiroZ: number
  eixoTraseiroZ: number
  raioRoda: number
  larguraRoda: number
  /** extremo dianteiro (para-choque) */
  frenteZ: number
  /** extremo traseiro (para-choque) */
  traseiraZ: number
  /** altura do piso do habitáculo */
  pisoY: number
  /** altura do topo da soleira */
  topoSoleiraY: number
  /** altura da linha do teto */
  tetoY: number
  /** altura da linha de cintura (base dos vidros) */
  cinturaY: number
  /** redução da meia-largura estrutural entre cintura e teto, em metros por lado */
  recuoLateralTeto: number
  /** arco transversal discreto das colunas entre cintura e teto, em metros */
  arqueamentoLateralColunas: number
  /** altura do capô */
  capoY: number
  colunaA: { zBase: number; zTopo: number }
  colunaB: { z: number }
  /** borda traseira da coluna C, junto ao vidro traseiro; o montante é `montanteColunaC` */
  colunaC: { zBase: number; zTopo: number }
  teto: FaixaLongitudinal
  portaDianteira: FaixaLongitudinal
  portaTraseira: FaixaLongitudinal
  volumeTraseiro: 'hatch' | 'sedan'
  zonaAbsorcaoDianteira: FaixaLongitudinal
  zonaAbsorcaoTraseira: FaixaLongitudinal
  tracao: Tracao
  srs: ConfiguracaoSrs
  /** onde fica o gerador de gás das cortinas, quando o perfil as declara */
  geradorCortina: PosicaoGeradorCortina
  retencao: ConfiguracaoRetencao
  energia: ConfiguracaoEnergia
  ancoras: AncoraOcupante[]
}

const SRS_HATCH: ConfiguracaoSrs = {
  airbagMotorista: true,
  airbagPassageiro: true,
  airbagLateralEsquerdo: true,
  airbagLateralDireito: true,
  airbagCortinaEsquerdo: true,
  airbagCortinaDireito: true,
  // fora deste perfil didático: a maquete não deve sugerir que todo carro os tenha
  airbagBancoMotorista: false,
  airbagColunaMotorista: false,
}

const RETENCAO_HATCH: ConfiguracaoRetencao = {
  motorista: { tipo: 'tresPontos', pretensionador: true },
  passageiro: { tipo: 'tresPontos', pretensionador: true },
  traseiroEsquerdo: { tipo: 'tresPontos', pretensionador: false },
  traseiroDireito: { tipo: 'tresPontos', pretensionador: false },
  // posição central com cinto subabdominal: é o caso que o aluno precisa
  // reconhecer, porque muda a leitura da cinemática naquele lugar
  traseiroCentral: { tipo: 'subabdominal', pretensionador: false },
}

const RETENCAO_SEDAN: ConfiguracaoRetencao = {
  ...RETENCAO_HATCH,
  // dois pretensionadores na frente (retrator e fecho): o hatch fica só com o
  // de retrator, para o aluno ver que o arranjo muda de veículo para veículo
  motorista: { tipo: 'tresPontos', pretensionador: true, pretensionadorFecho: true },
  passageiro: { tipo: 'tresPontos', pretensionador: true, pretensionadorFecho: true },
  traseiroEsquerdo: { tipo: 'tresPontos', pretensionador: true },
  traseiroDireito: { tipo: 'tresPontos', pretensionador: true },
}

const SRS_SEDAN: ConfiguracaoSrs = {
  ...SRS_HATCH,
  airbagBancoMotorista: true,
  airbagColunaMotorista: true,
}

function ancoras(
  xLateral: number,
  zDianteira: number,
  zTraseira: number,
  yPelve: number,
  alturaAssento: number,
): AncoraOcupante[] {
  const zero = vetor(0, 0, 0)
  return [
    {
      id: 'motorista',
      nome: 'Motorista',
      posicao: vetor(xLateral, yPelve, zDianteira),
      rotacao: zero,
      alturaAssento,
      lado: 'esquerdo',
      fila: 'dianteira',
    },
    {
      id: 'passageiro',
      nome: 'Passageiro dianteiro',
      posicao: vetor(-xLateral, yPelve, zDianteira),
      rotacao: zero,
      alturaAssento,
      lado: 'direito',
      fila: 'dianteira',
    },
    {
      id: 'traseiroEsquerdo',
      nome: `Traseiro — ${rotuloLadoVeiculo('esquerdo')}`,
      posicao: vetor(xLateral + 0.04, yPelve + 0.02, zTraseira),
      rotacao: zero,
      alturaAssento: alturaAssento + 0.02,
      lado: 'esquerdo',
      fila: 'traseira',
    },
    {
      id: 'traseiroCentral',
      nome: 'Traseiro central',
      posicao: vetor(0, yPelve + 0.02, zTraseira),
      rotacao: zero,
      alturaAssento: alturaAssento + 0.02,
      lado: 'central',
      fila: 'traseira',
    },
    {
      id: 'traseiroDireito',
      nome: `Traseiro — ${rotuloLadoVeiculo('direito')}`,
      posicao: vetor(-xLateral - 0.04, yPelve + 0.02, zTraseira),
      rotacao: zero,
      alturaAssento: alturaAssento + 0.02,
      lado: 'direito',
      fila: 'traseira',
    },
  ]
}

export const HATCH_COMPACTO: PerfilVeiculo = {
  id: 'hatchCompacto',
  nome: 'Hatch compacto (maquete didática)',
  comprimento: 3.95,
  largura: 1.72,
  altura: 1.54,
  entreEixos: 2.5,
  eixoDianteiroZ: 1.26,
  eixoTraseiroZ: -1.24,
  raioRoda: 0.31,
  larguraRoda: 0.2,
  frenteZ: 2.02,
  traseiraZ: -1.93,
  pisoY: 0.3,
  topoSoleiraY: 0.46,
  tetoY: 1.52,
  cinturaY: 0.92,
  recuoLateralTeto: 0.10,
  arqueamentoLateralColunas: 0.018,
  capoY: 0.94,
  colunaA: { zBase: 1.0, zTopo: 0.36 },
  colunaB: { z: -0.26 },
  colunaC: { zBase: -1.72, zTopo: -1.20 },
  teto: { zDianteiro: 0.36, zTraseiro: -1.20 },
  portaDianteira: { zDianteiro: 0.76, zTraseiro: -0.26 },
  portaTraseira: { zDianteiro: -0.26, zTraseiro: -0.96 },
  volumeTraseiro: 'hatch',
  tracao: 'dianteira',
  zonaAbsorcaoDianteira: { zDianteiro: 2.02, zTraseiro: 1.36 },
  zonaAbsorcaoTraseira: { zDianteiro: -1.55, zTraseiro: -1.93 },
  srs: SRS_HATCH,
  geradorCortina: 'colunaC',
  retencao: RETENCAO_HATCH,
  // hatch de frota urbana com conversão a gás: é o caso que mais aparece na
  // ocorrência no Brasil, e o cilindro no porta-malas muda o corte da traseira
  energia: {
    propulsao: 'combustao',
    tanqueCombustivel: true,
    cilindroGnv: true,
    bateriaAltaTensao: false,
  },
  ancoras: ancoras(0.36, -0.02, -0.92, 0.5, 0.42),
}

export const SEDAN_MEDIO: PerfilVeiculo = {
  id: 'sedanMedio',
  nome: 'Sedan médio (maquete didática)',
  comprimento: 4.62,
  largura: 1.78,
  altura: 1.54,
  entreEixos: 2.68,
  eixoDianteiroZ: 1.26,
  eixoTraseiroZ: -1.42,
  raioRoda: 0.32,
  larguraRoda: 0.21,
  frenteZ: 2.3,
  traseiraZ: -2.32,
  pisoY: 0.3,
  topoSoleiraY: 0.46,
  tetoY: 1.53,
  cinturaY: 0.93,
  recuoLateralTeto: 0.10,
  arqueamentoLateralColunas: 0.018,
  capoY: 0.95,
  colunaA: { zBase: 1.1, zTopo: 0.44 },
  colunaB: { z: -0.24 },
  colunaC: { zBase: -1.74, zTopo: -1.32 },
  teto: { zDianteiro: 0.44, zTraseiro: -1.32 },
  portaDianteira: { zDianteiro: 0.84, zTraseiro: -0.24 },
  portaTraseira: { zDianteiro: -0.24, zTraseiro: -1.12 },
  volumeTraseiro: 'sedan',
  tracao: 'dianteira',
  zonaAbsorcaoDianteira: { zDianteiro: 2.3, zTraseiro: 1.5 },
  zonaAbsorcaoTraseira: { zDianteiro: -1.8, zTraseiro: -2.32 },
  srs: SRS_SEDAN,
  // posição diferente do hatch de propósito: o aluno não deve decorar um lugar
  geradorCortina: 'trilhoMeio',
  retencao: RETENCAO_SEDAN,
  // sedan híbrido: põe o pacote de alta tensão sob o assoalho. Com o hatch a
  // gás, as duas famílias de risco ficam demonstráveis só trocando o veículo
  // no painel de Cena, sem precisar de um terceiro perfil
  energia: {
    propulsao: 'hibrido',
    tanqueCombustivel: true,
    cilindroGnv: false,
    bateriaAltaTensao: true,
  },
  ancoras: ancoras(0.38, 0.0, -1.0, 0.5, 0.42),
}

export const PERFIS_VEICULO: Record<PerfilVeiculo['id'], PerfilVeiculo> = {
  hatchCompacto: HATCH_COMPACTO,
  sedanMedio: SEDAN_MEDIO,
}

export function perfilVeiculoPorId(id: string): PerfilVeiculo {
  return PERFIS_VEICULO[id as PerfilVeiculo['id']] ?? HATCH_COMPACTO
}

const ALIAS_ANCORAS: Record<string, string> = {
  passageiroDianteiro: 'passageiro',
  passageiroTraseiroEsquerdo: 'traseiroEsquerdo',
  passageiroTraseiroDireito: 'traseiroDireito',
  passageiroTraseiroCentral: 'traseiroCentral',
}

export function ancoraPorId(perfil: PerfilVeiculo, id: string): AncoraOcupante {
  const idNormalizado = ALIAS_ANCORAS[id] ?? id
  const encontrada = perfil.ancoras.find((a) => a.id === idNormalizado)
  if (!encontrada) throw new Error(`Âncora de ocupante desconhecida: ${id}`)
  return encontrada
}

/** Base do painel lateral da carroceria (limite inferior da silhueta). */
export const Y_BASE_LATERAL = 0.14

/** Topo do painel lateral: um friso acima da linha de cintura. */
export const yTopoLateral = (perfil: PerfilVeiculo): number => perfil.cinturaY + 0.04

/** Raio do vão de roda, com folga de 7 cm ao redor do pneu. */
export const raioArcoRoda = (perfil: PerfilVeiculo): number => perfil.raioRoda + 0.07

/**
 * Altura máxima que o recorte do arco de roda alcança dentro de um vão de porta.
 *
 * O painel lateral é uma chapa única com os vãos das portas recortados e os
 * arcos de roda centrados nos eixos. A base do vão acompanha essa altura
 * para conservar uma faixa de chapa entre a abertura e a caixa de roda.
 */
export function alturaMaximaDoArcoNoVao(perfil: PerfilVeiculo, vao: FaixaLongitudinal): number {
  const r = raioArcoRoda(perfil)
  let maxima = Y_BASE_LATERAL
  for (const centroZ of [perfil.eixoDianteiroZ, perfil.eixoTraseiroZ]) {
    const distancia = Math.max(
      0,
      Math.min(Math.abs(vao.zDianteiro - centroZ), Math.abs(vao.zTraseiro - centroZ)),
    )
    const dentroDoVao = centroZ <= vao.zDianteiro && centroZ >= vao.zTraseiro
    const dz = dentroDoVao ? 0 : distancia
    if (dz >= r) continue
    maxima = Math.max(maxima, perfil.raioRoda + Math.sqrt(r * r - dz * dz))
  }
  return maxima
}

/** A borda inferior do vão sobe junto à roda traseira, preservando a chapa entre ambos. */
export function alturaBaseVaoPorta(perfil: PerfilVeiculo, vao: FaixaLongitudinal): number {
  return Math.max(perfil.topoSoleiraY, alturaMaximaDoArcoNoVao(perfil, vao) + 0.05)
}

/** Folga entre o arco de roda e a base efetiva do vão da porta, em metros. */
export function folgaArcoVao(perfil: PerfilVeiculo): number {
  return Math.min(
    alturaBaseVaoPorta(perfil, perfil.portaDianteira) - alturaMaximaDoArcoNoVao(perfil, perfil.portaDianteira),
    alturaBaseVaoPorta(perfil, perfil.portaTraseira) - alturaMaximaDoArcoNoVao(perfil, perfil.portaTraseira),
  )
}

/** Meia-largura útil da carroceria (centro das chapas laterais). */
export const meiaLargura = (perfil: PerfilVeiculo): number => perfil.largura / 2

/**
 * Meia-largura da gaiola estrutural: as colunas ficam um pouco dentro da chapa.
 * Exportado para que a cena e o cálculo de obstáculos usem o mesmo valor.
 */
export const xDaEstrutura = (perfil: PerfilVeiculo, sinal: number): number =>
  sinal * (perfil.largura / 2 - 0.08)

/** Abaixo da cintura a estrutura mantém sua largura; acima dela converge com arco suave. */
export function xDaEstruturaNaAltura(perfil: PerfilVeiculo, sinal: number, y: number): number {
  const vao = perfil.tetoY - perfil.cinturaY
  const t = vao <= 0 ? 0 : limitar((y - perfil.cinturaY) / vao, 0, 1)
  const arco = perfil.arqueamentoLateralColunas * Math.sin(Math.PI * t)
  return xDaEstrutura(perfil, sinal) - sinal * (perfil.recuoLateralTeto * t - arco)
}

/** Largura do painel externo: pequena cobertura sobre as longarinas do teto. */
export const larguraPainelTeto = (perfil: PerfilVeiculo): number =>
  2 * Math.abs(xDaEstruturaNaAltura(perfil, 1, perfil.tetoY)) + 0.08

/**
 * Geometria do cofre do motor e da estrutura dianteira.
 *
 * Nenhum destes valores vira campo do perfil porque nenhum deles é escolha de
 * projeto independente: todos derivam da coluna A, do eixo dianteiro e das
 * zonas de absorção já declaradas. Seguem a mesma regra dos demais helpers
 * daqui — a cena consome, nunca recalcula.
 */

/** Painel corta-fogo: separa o cofre do motor do habitáculo, à frente da coluna A. */
export const zParaFogo = (perfil: PerfilVeiculo): number => perfil.colunaA.zBase + 0.08

/**
 * Envelope do cofre do motor.
 *
 * Não coincide com `zonaAbsorcaoDianteira`: num carro real o motor ocupa o
 * balanço dianteiro e a zona de absorção passa ao redor e à frente dele. A
 * folga na ponta é o que sobra para radiador e para-choque.
 */
export const cofreMotor = (perfil: PerfilVeiculo): FaixaLongitudinal => ({
  zDianteiro: perfil.frenteZ - 0.42,
  zTraseiro: zParaFogo(perfil),
})

/**
 * Caixa de ar (plenum) na base do para-brisa.
 *
 * É a calha entre a quina traseira do capô e o vidro, onde ficam limpadores e a
 * tomada de ar da ventilação. Interessa ao salvamento porque fica exatamente
 * sobre a travessa do corta-fogo, junto ao pé da coluna A.
 */
export const zCaixaDeAr = (perfil: PerfilVeiculo): number => perfil.colunaA.zBase - 0.02

/** Torre do amortecedor dianteiro: onde a suspensão se apoia no monobloco. */
export const torreSuspensao = (
  perfil: PerfilVeiculo,
  sinal: number,
): { x: number; y: number; z: number } => ({
  x: sinal * (perfil.largura / 2 - 0.24),
  y: perfil.topoSoleiraY + 0.34,
  z: perfil.eixoDianteiroZ,
})

/**
 * Meia-distância entre as longarinas.
 *
 * Longarina fica recuada da soleira por uma folga aproximadamente constante,
 * não por uma fração da largura: por isso o desconto é fixo e a largura do
 * veículo é que varia.
 */
export const xDaLongarina = (perfil: PerfilVeiculo, sinal: number): number =>
  sinal * (perfil.largura / 2 - 0.44)

/** Altura do plano das longarinas e do assoalho estrutural. */
export const yDaLongarina = (perfil: PerfilVeiculo): number => perfil.pisoY - 0.06

/** Pé da borda posterior da coluna, atrás da caixa de roda e sobre a longarina. */
export function baseColunaTraseira(perfil: PerfilVeiculo, sinal: number) {
  const z = Math.min(perfil.colunaC.zBase, perfil.eixoTraseiroZ - raioArcoRoda(perfil) - 0.08)
  const inicio = perfil.zonaAbsorcaoTraseira.zDianteiro
  const fim = perfil.traseiraZ + 0.14
  const fracao = Math.max(0, Math.min(1, (z - inicio) / (fim - inicio)))
  return { x: xDaEstrutura(perfil, sinal), y: yDaLongarina(perfil) + 0.16 * fracao, z }
}

/** Túnel central: passa sobre o escapamento e, quando há, sobre o cardã. */
export const tunelCentral = (perfil: PerfilVeiculo): { largura: number; altura: number } => ({
  largura: perfil.largura * 0.2,
  altura: 0.16,
})

export type IdColuna = 'A' | 'B' | 'C'

/** Seção de uma coluna: largura ao longo do carro (z) e espessura para dentro (x). */
export interface SecaoColuna {
  largura: number
  espessura: number
  /** largura do pé, abaixo da cintura; ausente = igual à largura */
  larguraPe?: number
}

export const SECOES_COLUNA: Readonly<Record<IdColuna, SecaoColuna>> = {
  A: { largura: 0.12, espessura: 0.11 },
  B: { largura: 0.14, espessura: 0.12, larguraPe: 0.18 },
  C: { largura: 0.14, espessura: 0.13 },
}

export const secaoColuna = (id: IdColuna): SecaoColuna => SECOES_COLUNA[id]

/** Metade da maior dimensão da seção: folga de passagem em torno do eixo da coluna. */
export const raioColuna = (id: IdColuna): number =>
  Math.max(SECOES_COLUNA[id].largura, SECOES_COLUNA[id].espessura) / 2

/** Espessura da chapa do teto. */
export const ESPESSURA_PAINEL_TETO = 0.05

/**
 * Altura da face interna do teto.
 *
 * É a superfície sobre a qual a vítima fica deitada no veículo capotado, e é o
 * teto a partir do qual se mede o vão até os bancos. A chapa é desenhada
 * centrada em `tetoY`, então a face interna fica meia espessura abaixo. Mora
 * aqui para que a cena e o cálculo usem a mesma chapa.
 */
export const yFaceInternaDoTeto = (perfil: PerfilVeiculo): number =>
  perfil.tetoY - ESPESSURA_PAINEL_TETO / 2

/** Altura da tampa traseira do hatch: bem abaixo da linha de cintura, na quina do para-choque. */
export const Y_TAMPA_TRASEIRA_BAIXA = 0.6

/** Abertura da tampa traseira, em radianos em torno de X: positivo levanta a borda de baixo para cima e para trás. */
export const ABERTURA_RAD_TAMPA = 1.2

/**
 * Eixo de articulação da tampa traseira, no plano de simetria.
 *
 * No hatch a tampa é o conjunto vidro + chapa baixa e gira na quina de trás do
 * teto, como a de um carro de verdade. No sedan só o porta-malas gira, na borda
 * de trás do vidro, que fica fixo na carroceria.
 */
export function eixoDaTampaTraseira(perfil: PerfilVeiculo): readonly [number, number, number] {
  return perfil.volumeTraseiro === 'hatch'
    ? [0, perfil.tetoY - 0.03, perfil.colunaC.zTopo - 0.01]
    : [0, perfil.cinturaY, perfil.colunaC.zBase - 0.02]
}

/** Pontos de articulação e de fechamento da tampa (as mesmas contas da Carroceria). */
export function extremosTampaTraseira(perfil: PerfilVeiculo): { de: Vetor3; para: Vetor3 } {
  const hatch = perfil.volumeTraseiro === 'hatch'
  return {
    de: hatch
      ? vetor(0, perfil.cinturaY - 0.04, perfil.colunaC.zBase - 0.02)
      : vetor(0, perfil.cinturaY, perfil.colunaC.zBase - 0.02),
    para: hatch
      ? vetor(0, Y_TAMPA_TRASEIRA_BAIXA, perfil.traseiraZ + 0.04)
      : vetor(0, perfil.cinturaY + 0.06, perfil.traseiraZ + 0.05),
  }
}

/** Largura de cada quina traseira fixa da carroceria no hatch. */
export const LARGURA_QUINA_TRASEIRA_HATCH = 0.20

/**
 * Largura da tampa traseira.
 * No hatch a tampa é mais estreita para acomodar as quinas traseiras fixas;
 * no sedan a tampa e a saia cobrem a traseira de lado a lado.
 */
export const larguraTampaTraseira = (perfil: PerfilVeiculo): number =>
  perfil.volumeTraseiro === 'hatch'
    ? perfil.largura - 2 * LARGURA_QUINA_TRASEIRA_HATCH
    : perfil.largura

/** Altura do ponto mais alto da traseira: a tampa baixa no hatch, a linha de cintura no sedan (porta-malas). */
export const alturaTampaTraseira = (perfil: PerfilVeiculo): number =>
  perfil.volumeTraseiro === 'hatch' ? Y_TAMPA_TRASEIRA_BAIXA : perfil.cinturaY

/**
 * Posição longitudinal de uma coluna a uma dada altura.
 *
 * As colunas A e C são inclinadas: usar o `z` da base para ancorar algo acima
 * da linha de cintura coloca a peça fora da carroceria. A coluna B é vertical
 * e o cálculo degenera corretamente para o `z` único dela.
 */
export function zDaColunaNaAltura(
  perfil: PerfilVeiculo,
  coluna: { zBase: number; zTopo: number },
  y: number,
): number {
  const vao = perfil.tetoY - perfil.cinturaY
  const t = vao <= 0 ? 0 : limitar((y - perfil.cinturaY) / vao, 0, 1)
  return coluna.zBase + (coluna.zTopo - coluna.zBase) * t
}

/**
 * z da coluna A em qualquer altura, da soleira ao teto.
 *
 * Abaixo da linha de cintura (`y < cinturaY`), a coluna das dobradiças desce
 * a prumo em `portaDianteira.zDianteiro`, onde a porta dianteira gira e suas
 * dobradiças ficam fixadas.
 *
 * A partir da cintura (`y >= cinturaY`), sobe inclinada acompanhando o
 * para-brisa (`colunaA.zBase` até `colunaA.zTopo`).
 *
 * A descontinuidade na cintura (`y = cinturaY`) é intencional: representa
 * o encontro estrutural horizontal com o painel / para-lama na base do
 * para-brisa.
 */
export function zDaColunaANaAltura(perfil: PerfilVeiculo, y: number): number {
  if (y >= perfil.cinturaY) {
    return zDaColunaNaAltura(perfil, perfil.colunaA, y)
  }
  return perfil.portaDianteira.zDianteiro
}

/**
 * Montante principal da coluna C.
 *
 * `perfil.colunaC` descreve a borda traseira da coluna, onde ela encontra o
 * vidro traseiro e fecha o habitáculo. O membro estrutural que carrega o teto,
 * porém, desce pelo batente da porta traseira até a soleira, À FRENTE da roda
 * traseira e sobe quase a prumo, como a borda de trás da janela da porta num
 * hatch ou sedan real. Entre ele e a borda traseira fica a chapa pintada da
 * coluna C, sem vidro: larga na cintura, sobre a caixa de roda, e com cerca de
 * 40% do comprimento da janela no teto (Argo, Onix).
 *
 * O topo do montante é limitado em relação à borda traseira da coluna C
 * (`Math.max(portaTraseira.zTraseiro - 0.12, colunaC.zTopo + 0.08)`) para que a
 * coluna C continue com ~18 cm no teto no hatch com teto encurtado, sem alterar
 * a geometria do sedan.
 *
 * `zBase`/`zTopo` seguem a convenção das outras colunas (cintura e teto);
 * `zPe` é o encontro com a soleira.
 */
export function montanteColunaC(perfil: PerfilVeiculo): { zBase: number; zTopo: number; zPe: number } {
  return {
    // o pé fica à frente do pneu: a porta traseira contorna a caixa de roda e o
    // montante sobe curvando para trás até o batente
    zPe: perfil.eixoTraseiroZ + perfil.raioRoda + 0.04,
    zBase: perfil.portaTraseira.zTraseiro - 0.06,
    // quase a prumo: 6 cm para trás entre a cintura e o teto, mantendo largura da C
    zTopo: Math.max(perfil.portaTraseira.zTraseiro - 0.12, perfil.colunaC.zTopo + 0.08),
  }
}

/** Coluna que sustenta a ancoragem superior do cinto naquela fila. */
export function colunaDaRetencao(
  perfil: PerfilVeiculo,
  fila: AncoraOcupante['fila'],
): { zBase: number; zTopo: number } {
  return fila === 'dianteira'
    ? { zBase: perfil.colunaB.z, zTopo: perfil.colunaB.z }
    : montanteColunaC(perfil)
}

/** Sufixo de id usado pelo catálogo para as peças de uma âncora. */
export const sufixoAncora = (id: string): string => id.charAt(0).toUpperCase() + id.slice(1)

export function retencaoDaAncora(perfil: PerfilVeiculo, idAncora: string): ConfiguracaoCinto | null {
  return perfil.retencao[idAncora] ?? null
}

/** Âncoras que este perfil declara com conjunto de retenção, na ordem das âncoras. */
export function ancorasComCinto(perfil: PerfilVeiculo): AncoraOcupante[] {
  return perfil.ancoras.filter((a) => perfil.retencao[a.id] != null)
}

/** Lista dos dispositivos SRS ativos no perfil, já em ordem de exibição. */
export function srsAtivos(perfil: PerfilVeiculo): Array<keyof ConfiguracaoSrs> {
  return (Object.keys(perfil.srs) as Array<keyof ConfiguracaoSrs>).filter((k) => perfil.srs[k])
}
